import { useCallback, useMemo, useState } from 'react';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { P2GButton, P2GGrid, P2GText, P2GTextBox, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { GetPrePayBalanceResponse } from '~/lib/api/me/types';
import { usePaymentApi } from '~/lib/api/payment/use-payment-api';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import PrePayBalanceDisplay from './prepay-balance-display';
import useOnLoad from '~/routes/use-on-load';

const PrePayTopup = () => {
  const { generatePrePayLink } = usePaymentApi();
  const { t } = useTranslation(TranslationNamespaces.PREPAY);
  const { getPrePayBalance } = useMeApi();
  const [topup, setTopup] = useState<number>(0);
  const [balance, setBalance] = useState<GetPrePayBalanceResponse>();
  const isXs = useP2GMediaQueryOnly('xs');
  const [loadingTopup, setLoadingTopup] = useState<boolean>(false);

  const minTopup = balance?.prePayMinimumTopUp ?? 0;

  useOnLoad(() => {
    const load = async () => {
      const balanceResponse = await getPrePayBalance();
      if (balanceResponse.success && balanceResponse.result) {
        setBalance(balanceResponse.result);
      }
    };
    load();
  });

  const doTopup = useCallback(
    async (topupAmount: number) => {
      setLoadingTopup(true);
      const res = await generatePrePayLink(topupAmount * 100, balance?.currencyCode ?? 'GBP');
      if (res.success) {
        window.location.href = res.result.link;
      } else {
        setLoadingTopup(false);
      }
    },
    [generatePrePayLink, balance]
  );

  const quickOptions = useMemo(() => [20, 50, 100, 250].filter((amount) => amount >= minTopup), [minTopup]);

  return (
    <P2GGrid container direction="column" spacing={2}>
      {balance && (
        <>
          <P2GGrid item>
            <PrePayBalanceDisplay balance={balance} includePromotion={true} />
          </P2GGrid>
          <P2GGrid item>
            <P2GText component="p" mb={1}>
              <strong>{t('QUICK_SELECT_AMOUNT', { defaultValue: 'Quick Select Topup Amount' })}</strong>
            </P2GText>
            {quickOptions.length > 0 && (
              <P2GGrid container spacing={1}>
                {quickOptions.map((amt) => (
                  <P2GGrid item key={amt} sm="auto">
                    <P2GButton
                      variant={amt === topup ? 'secondary' : 'tertiary'}
                      onClick={() => {
                        setTopup(amt);
                        doTopup(amt);
                      }}
                      disabled={loadingTopup}
                      fullWidth
                    >
                      {toDisplayCurrency(balance?.currencyCode ?? 'GBP', amt, 0)}
                    </P2GButton>
                  </P2GGrid>
                ))}
              </P2GGrid>
            )}
          </P2GGrid>
        </>
      )}
      <P2GGrid item>
        <P2GText component="p" mb={1}>
          <strong>{t('OR_ENTER_CUSTOM_AMOUNT', { defaultValue: 'Or Enter Custom Amount' })}</strong>
        </P2GText>
        <P2GTextBox
          id="PrePayAmount"
          required
          label={t('ADD_CREDIT_INPUT_LABEL', { defaultValue: 'Topup Amount' })}
          value={topup == 0 ? '' : topup}
          onChange={(e) => setTopup(Number(e))}
          min={minTopup}
          type="number"
          fullWidth
          endAdornment={
            <P2GButton
              variant="primary"
              disabled={!topup || topup < minTopup || loadingTopup}
              fullWidth={isXs}
              onClick={() => doTopup(topup)}
            >
              {loadingTopup
                ? t('LOADING', { defaultValue: 'Loading...' })
                : t('ADD_CREDIT_BUTTON', { defaultValue: 'Add Credit' })}
            </P2GButton>
          }
        />
      </P2GGrid>
      {balance && (
        <P2GGrid item>
          <P2GText variant="caption">
            <Trans
              i18nKey="MINIMUM_TOPUP"
              t={t}
              values={{ minAmount: toDisplayCurrency(balance.currencyCode, minTopup, 0) }}
              components={{ b: <strong /> }}
              defaults="* Prepayments must be for a minimum of <b>{{minAmount}}</b>."
            />
          </P2GText>
        </P2GGrid>
      )}
    </P2GGrid>
  );
};

export default PrePayTopup;
